import { configureStore } from '@reduxjs/toolkit';
import type { AnyAction, StateFromReducersMapObject } from '@reduxjs/toolkit';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import { PageType } from '@vgtv/api-client/lib/page';
import type { Page, TemplatePage } from '@vgtv/api-client/lib/page';
import type { CombinedState, PreloadedState } from 'redux';
// eslint-disable-next-line import/no-unresolved
import type { WritableDraft } from 'immer/dist/internal';
// eslint-disable-next-line import/no-unresolved
import type { NoInfer } from '@reduxjs/toolkit/dist/tsHelpers';

import { assetsReducer, storeAssetsFromPageComponentsPreset } from './assets';
import type { AssetsState } from './assets';
import {
  categoriesReducer,
  storeCategoriesFromPageComponentsPreset,
} from './categories';
import type { CategoriesState } from './categories';
import { profilesReducer } from './profiles';
import {
  pagesReducer,
  storePageByCategoryId,
  storePageBySlug,
  storePageByStoryId,
  storePageByTagId,
} from './pages';
import type { PagesState } from './pages';
import { componentsReducer, storePageComponentsWithPreset } from './components';
import type { ComponentsState } from './components';
import { uiReducer } from './ui';
import { fetchingReducer } from './fetching';
import { watchtimeReducer } from './watchtime';
import { favouritesReducer } from './favourites';
import { searchReducer } from './search';

const reducer = {
  pages: pagesReducer,
  components: componentsReducer,
  assets: assetsReducer,
  categories: categoriesReducer,
  profiles: profilesReducer,
  ui: uiReducer,
  fetching: fetchingReducer,
  watchtime: watchtimeReducer,
  favourites: favouritesReducer,
  search: searchReducer,
};

export type RootState = StateFromReducersMapObject<typeof reducer>;

function createStore(
  asset?: SvpAssetAdditional,
  pageConfig?: Exclude<Page, TemplatePage>
) {
  const preloadedState = getInitialState(asset, pageConfig);

  return configureStore({
    reducer,
    preloadedState,
  });
}

export type AppDispatch = ReturnType<typeof createStore>['dispatch'];

function getInitialState(
  asset?: SvpAssetAdditional,
  pageConfig?: Exclude<Page, TemplatePage>
): PreloadedState<CombinedState<NoInfer<RootState>>> {
  const store: PreloadedState<CombinedState<NoInfer<RootState>>> = {};

  store.categories = {
    vgtv: {
      items: {},
      categoryMappings: {},
    },
    ab: {
      items: {},
      categoryMappings: {},
    },
    brandstudio: {
      items: {},
      categoryMappings: {},
    },
  };

  if (asset) {
    const { category } = asset;

    store.assets = {
      vgtv: {},
      ab: {},
      brandstudio: {},
    };

    store.assets[asset.provider][asset.id] = asset;

    store.categories[asset.provider].items[category.id] = category;
  }

  if (pageConfig) {
    const vendor = pageConfig.provider;

    // this has to be manually aligned with the initial pages store since it's an object
    store.pages = {
      vgtv: {
        byCategoryId: {},
        byTagId: {},
        byStoryId: {},
        bySlug: {},
      },
      ab: {
        byCategoryId: {},
        byTagId: {},
        byStoryId: {},
        bySlug: {},
      },
      brandstudio: {
        byCategoryId: {},
        byTagId: {},
        byStoryId: {},
        bySlug: {},
      },
    };

    if (pageConfig.type === PageType.CATEGORY) {
      const category = pageConfig.category;

      storePageByCategoryId(store.pages as PagesState, {
        type: 'synthetic',
        payload: pageConfig,
      });

      store.categories[vendor].items[category.id] = category;
    }

    if (pageConfig.type === PageType.TAG) {
      storePageByTagId(store.pages as PagesState, {
        type: 'synthetic',
        payload: pageConfig,
      });
    }

    if (pageConfig.type === PageType.STORY) {
      storePageByStoryId(store.pages as PagesState, {
        type: 'synthetic',
        payload: pageConfig,
      });
    }

    if (pageConfig.type === PageType.CUSTOM) {
      storePageBySlug(store.pages as PagesState, {
        type: 'synthetic',
        payload: pageConfig,
      });
    }

    store.components = store.components || {};
    storePageComponentsWithPreset(store.components as ComponentsState, {
      type: 'synthetic',
      payload: pageConfig,
    });

    store.assets = store.assets || { vgtv: {}, ab: {}, brandstudio: {} };
    storeAssetsFromPageComponentsPreset(
      store.assets as WritableDraft<AssetsState>,
      {
        payload: pageConfig,
      } as AnyAction & { payload: typeof pageConfig }
    );

    storeCategoriesFromPageComponentsPreset(
      store.categories as WritableDraft<CategoriesState>,
      {
        payload: pageConfig,
      } as AnyAction & { payload: typeof pageConfig }
    );
  }

  return store;
}

export { createStore, reducer };
